// Helpers PUROS del modo directo de "Crear desde cero".
//
// El modo directo no le pega al LLM: traduce el estado de la UI (prompt,
// tipo de salida, formato, duración, chips de adjuntos) al input de una
// tool card, que después se empuja a mano con `chat.pushToolCard` — mismo
// mecanismo scripted que usa "Sorpréndeme" (InterfazV2). Toda la lógica
// testeable vive acá; los componentes solo la consumen.
//
// Ver ADR-2026-08-19-modo-directo-crear-desde-cero.

import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import {
  creditsForImage,
  creditsForVideoGeneral,
  safeResolution,
  VIDEO_BASICA_MAX_S,
  VIDEO_PROFESIONAL_MAX_S,
  type VideoResolution,
} from '../../../../utils/credits';

export type OutputKind = 'imagen' | 'video';
/** Calidad del video del asistente (define proveedor y tope de duración). */
export type VideoQuality = 'basica' | 'profesional';

/** Piso de duración del video en modo directo (segundos), para el slider.
 *  El motor acepta cualquier número (`AgentVideoConfig.durationSeconds`,
 *  default 5); este es el límite que ofrecemos en la UI. */
export const VIDEO_DURATION_MIN = 5;

/** Tope del slider según la calidad: básica 15s (Seedance 2), profesional
 *  30s (Seedance 2.5, DEFAULT desde 2026-08-30). Mismos topes que la card y
 *  el motor (utils/credits). */
export function videoDuracionMax(quality: VideoQuality | undefined): number {
  return quality === 'basica' ? VIDEO_BASICA_MAX_S : VIDEO_PROFESIONAL_MAX_S;
}

/** Normaliza la duración al rango del slider de la calidad activa. */
export function clampDuracion(
  s: number | undefined,
  quality?: VideoQuality,
): number {
  return Math.max(
    VIDEO_DURATION_MIN,
    Math.min(videoDuracionMax(quality), Math.round(s ?? VIDEO_DURATION_MIN)),
  );
}

/** Tope de imágenes por tanda — espejo de `FREEFORM_MAX_CANTIDAD` de
 *  agentGenerate, que es quien hace el clamp de verdad. */
export const MAX_CANTIDAD = 6;

/** Tope de referencias que acepta el motor (mismo que usa la tool card). */
const MAX_REFERENCES = 6;

export interface DirectCallArgs {
  outputKind: OutputKind;
  prompt: string;
  formatSlug: string;
  /** Imágenes a generar en la tanda (solo aplica a `outputKind: 'imagen'`). */
  cantidad?: number;
  durationSeconds: number;
  /** Calidad del video elegida en la ruedita (default 'basica'). */
  videoQuality?: VideoQuality;
  /** Resolución del video elegida en la ruedita (default '720p'). */
  videoResolution?: VideoResolution;
  attachments: ChatAttachment[];
}

export interface DirectToolCall {
  kind: 'generate_image' | 'generate_video';
  input: Record<string, any>;
}

/** Chips del composer → `reference_images` del schema de las tools.
 *  Las imágenes subidas viajan como `{source:'adjunto'}`: la card las
 *  resuelve desde `getReferenceImages()` del hook, no desde un id. */
function referencesFromAttachments(
  attachments: ChatAttachment[],
): Array<Record<string, string>> {
  const refs: Array<Record<string, string>> = [];
  for (const att of attachments) {
    if (att.kind === 'product') {
      refs.push({ source: 'producto', product_id: att.productId });
    } else if (att.kind === 'model') {
      // OJO: `ModelSelection` discrimina por `mode`, no por `kind`.
      // Solo los modelos del catálogo ('preset') tienen id que el motor
      // pueda resolver; 'auto' / 'custom' / 'upload' no aportan referencia.
      const sel = att.selection;
      if (sel && sel.mode === 'preset' && sel.id != null) {
        refs.push({ source: 'modelo', model_id: String(sel.id) });
      }
    } else if (att.kind === 'image') {
      refs.push({ source: 'adjunto' });
    }
  }
  return refs.slice(0, MAX_REFERENCES);
}

/** Normaliza la cantidad al rango que acepta el motor. */
export function clampCantidad(n: number | undefined): number {
  return Math.max(1, Math.min(MAX_CANTIDAD, Math.floor(n ?? 1)));
}

/** Traduce el estado del modo directo a la tool card a empujar. */
export function buildDirectToolCall(args: DirectCallArgs): DirectToolCall {
  const { outputKind, prompt, formatSlug, durationSeconds, attachments } = args;
  const refs = referencesFromAttachments(attachments);
  const isVideo = outputKind === 'video';
  const cantidad = clampCantidad(args.cantidad);

  return {
    kind: isVideo ? 'generate_video' : 'generate_image',
    input: {
      prompt: prompt.trim(),
      format: formatSlug,
      // En modo directo TODO autogenera (imagen y video): la pill del composer
      // ya mostró el costo y tocar Generar ES la confirmación — la regla
      // ADR-146/147 ("el video nunca se genera sin ver el costo") se cumple
      // ahí. `direct: true` es lo que habilita a la card a autogenerar video
      // (con el agente, sin ese flag, sigue pidiendo confirmación).
      autogenerate: true,
      ...(isVideo
        ? {
            direct: true,
            duration: durationSeconds,
            // Siempre explícitos (con defaults: profesional/720p desde
            // 2026-08-30) para que el resumen de la card muestre exactamente
            // lo elegido en la ruedita.
            quality: args.videoQuality ?? 'profesional',
            resolution: safeResolution(args.videoResolution),
          }
        : // Solo mandamos `cantidad` cuando es >1: así una generación simple
          // queda con el mismo input que antes de existir el control.
          cantidad > 1
          ? { cantidad }
          : {}),
      ...(refs.length > 0 ? { reference_images: refs } : {}),
    },
  };
}

/** Créditos que va a costar la generación, para mostrarlos ANTES de tocar
 *  Generar. Misma fuente de precios que el Estudio de Video y que la card
 *  de resumen del video (`utils/credits`): la tarifa sale de calidad ×
 *  resolución (básica → standard, profesional → pro / Seedance 2.5). */
export function creditsForDirectCall(args: {
  outputKind: OutputKind;
  cantidad?: number;
  durationSeconds: number;
  videoQuality?: VideoQuality;
  videoResolution?: VideoResolution;
}): number {
  if (args.outputKind === 'video') {
    return creditsForVideoGeneral({
      durationSec: args.durationSeconds,
      model: args.videoQuality === 'basica' ? 'standard' : 'pro',
      resolution: safeResolution(args.videoResolution),
    });
  }
  return creditsForImage() * clampCantidad(args.cantidad);
}

/** Línea de contexto invisible que le avisa al agente qué eligió el usuario
 *  en el toggle. Es una PISTA, no una imposición: con Ayuda de IA prendida
 *  el agente sigue pudiendo conversar y ajustar. */
export function buildOutputKindHintLine(kind: OutputKind): string {
  return kind === 'video'
    ? 'El usuario tiene seleccionado "video" en el toggle — salvo que pida explícitamente otra cosa, generá un video.'
    : 'El usuario tiene seleccionado "imagen" en el toggle — salvo que pida explícitamente otra cosa, generá una imagen.';
}

/** Label compacto para la pill de formato: el ratio entre paréntesis
 *  ("Cuadrado (1:1)" → "1:1"). Los nombres completos no entran en la fila. */
export function formatShortLabel(id: string | null): string {
  const opt = FORMAT_OPTIONS_V2.find((f) => f.id === id);
  const ratio = opt?.name.match(/\(([^)]+)\)/)?.[1];
  return ratio ?? '1:1';
}
